import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import { useIntroduceMenusQuery } from "hooks";
import TopBanner from "../../../components/topBanner/topBanner";
import imgBaner from "../../../images/banner/banner---gioi-thieu.png";
import i18n from "../../../language/i18n";
import { normalizeLang } from "../../../utils/localeDataLoader";

const INTRODUCE_MENU_GROUPS = [
  {
    slugs: ["ban-lanh-dao", "leadership"],
    targets: { vi: "ban-lanh-dao", en: "leadership" },
    labels: { vi: "Ban lãnh đạo", en: "Leadership" },
  },
  {
    slugs: [
      "logo-gia-tri-nen-tang",
      "logo-foundational-values",
      "thong-diep",
      "message",
      "tam-nhin-su-menh-gia-tri-cot-loi",
      "vision-mission-values-core",
    ],
    targets: { vi: "logo-gia-tri-nen-tang", en: "logo-foundational-values" },
    labels: { vi: "Thông điệp", en: "Message" },
  },
  {
    slugs: ["moc-thoi-gian", "milestones"],
    targets: { vi: "moc-thoi-gian", en: "milestones" },
    labels: { vi: "Mốc thời gian / Cột mốc chính", en: "Milestones" },
  },
  {
    slugs: ["ho-so-nang-luc", "capability-profile"],
    targets: { vi: "ho-so-nang-luc", en: "capability-profile" },
    labels: { vi: "Hồ sơ năng lực", en: "Capability profile" },
  },
  {
    slugs: ["cong-ty-thanh-vien", "cong-ty-thanh-phan", "member-companies"],
    targets: { vi: "cong-ty-thanh-vien", en: "member-companies" },
    labels: { vi: "Công ty thành viên", en: "Member companies" },
  },
  {
    slugs: ["khach-hang-doi-tac", "customers-partners", "thuong-hieu-doi-tac", "partners"],
    targets: { vi: "khach-hang-doi-tac", en: "customers-partners" },
    labels: { vi: "Khách hàng và đối tác", en: "Customers and partners" },
  },
  {
    slugs: ["dinh-huong-phat-trien", "development-direction"],
    targets: { vi: "dinh-huong-phat-trien", en: "development-direction" },
    labels: { vi: "Định hướng phát triển", en: "Development direction" },
  },
];

const buildIntroduceMenuItems = (menus, language) =>
  INTRODUCE_MENU_GROUPS.map((group) => {
    const targetSlug =
      group.targets[language] || group.targets.en || group.slugs[0];
    const targetMenu = menus.find((item) => item.slug === targetSlug);
    const fallbackMenu = menus.find((item) => group.slugs.includes(item.slug));

    return {
      ...(targetMenu || fallbackMenu || {}),
      slug: targetSlug,
      slugs: group.slugs,
      name:
        targetMenu?.name ||
        fallbackMenu?.name ||
        group.labels?.[language] ||
        group.labels?.vi,
    };
  });

function Introduce() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentLang = normalizeLang(i18n.language);
  const { data: introduceMenus = [] } = useIntroduceMenusQuery(currentLang);

  const pages = useMemo(
    () => buildIntroduceMenuItems(introduceMenus, currentLang),
    [introduceMenus, currentLang],
  );

  const activePage = useMemo(() => {
    const pathParts = location.pathname.split("/").filter(Boolean).reverse();

    return (
      pages.find((page) =>
        pathParts.some(
          (part) => page.slug === part || page.slugs?.includes(part),
        ),
      ) || pages[0]
    );
  }, [location.pathname, pages]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const getMenuName = (viSlug, enSlug) => {
    const menu = introduceMenus.find(
      (item) => item.slug === viSlug || item.slug === enSlug,
    );
    return menu?.name || "";
  };

  const activePageName = activePage?.name || t("menu.about");

  return (
    <div className="introduce">
      <TopBanner
        imgBanner={imgBaner}
        pageName={t("menu.about")}
        titleCenter={activePageName}
        text={activePageName}
      />

      <main className="layout_container introduce-page">
        <div className="introduce__content introduce-page__content">
          <Outlet context={{ getMenuName }} />
        </div>
      </main>
    </div>
  );
}

export default Introduce;
